/**
 * VeriDoc core API — typed methods + domain types for the comparison/review
 * capability (ADR-119 D3 public surface).
 *
 * The wire format is camelCase (typescript/patterns.md): every field below
 * matches the JSON FastAPI emits.
 *
 * Boundary (ADR-118): document management + auth + upload + review-history live
 * in the host/demo (see the demo's `api-demo.ts`). This module holds only what
 * a host integrating `<ComparisonWorkspace>` consumes — comparison, version
 * detail (with parse tree), decisions, annotations, editing, source
 * verification, change analysis.
 */
import { api, veridocBaseUrl } from './transport';
import type { DocNode } from '../features/doc-viewer/DocViewer.types';

// ---------------------------------------------------------------------------
// Domain types — kept in sync with server/veridoc_core/schemas/library.py
// ---------------------------------------------------------------------------

export type VeriDocRole = 'lawyer' | 'reviewer' | 'viewer';

export interface CurrentUser {
  id: string;
  role: VeriDocRole;
  displayName: string;
}

export interface DocumentVersionSummary {
  id: string;
  versionLabel: string;
  description: string | null;
  fileFormat: 'pdf' | 'docx';
  fileSizeBytes: number;
  parseStatus: 'pending' | 'processing' | 'ready' | 'error';
  pageCount: number | null;
  createdAt: string;
  // F9 (ADR-117): present when parseStatus === 'error'. VD_SCANNED_UNSUPPORTED
  // = scanned PDF but OCR not enabled; VD_OCR_FAILED = OCR ran and failed.
  parseErrorCode?: string | null;
  parseErrorMessage?: string | null;
}

export interface DocumentVersionDetail extends DocumentVersionSummary {
  documentId: string;
  parsedDoc: DocNode | null;
}

/**
 * Stable parse error codes carried on `parseErrorCode` when a version fails to
 * parse (`parseStatus === 'error'`). Source of truth: docs/data-dictionary.md
 * §解析错误码 + veridoc_core/deps/ocr.py (ADR-117). Core parse contract — it
 * annotates `DocumentVersionSummary.parseErrorCode` (a core type), so it lives
 * in the core library even though only the demo upload flow reads it today.
 */
export const ParseErrorCode = {
  /** Scanned PDF (no text layer) but OCR not enabled on this deployment. */
  SCANNED_UNSUPPORTED: 'VD_SCANNED_UNSUPPORTED',
  /** OCR ran but raised / timed out / produced no text. */
  OCR_FAILED: 'VD_OCR_FAILED',
} as const;

export type ChangeType = 'addition' | 'deletion' | 'modification' | 'move' | 'format';

export type InlineDiffOp = 'eq' | 'add' | 'del' | 'fmt';

export interface InlineDiffSegment {
  op: InlineDiffOp;
  text: string;
  styleA?: string;
  styleB?: string;
}

/**
 * Decision status — the *persisted* lifecycle a single change can be in:
 *
 *   pending  → no decision row exists yet (default for every fresh change)
 *   accepted → reviewer accepted v2's wording
 *   rejected → reviewer rejected v2's wording
 *
 * The PUT endpoint accepts the *verb* (`accept` / `reject`); the persisted
 * value uses the *noun*. Re-asserting the current decision toggles back to
 * `pending` (the row is deleted) — see `DecisionMutationResponse`.
 */
export type DecisionStatus = 'pending' | 'accepted' | 'rejected';

export type DecisionAction = 'accept' | 'reject' | 'pending';

export interface ChangeResponse {
  id: string;
  comparisonId: string;
  changeType: ChangeType;
  positionA: number | null;
  positionB: number | null;
  anchorNodePathA: string | null;
  anchorNodePathB: string | null;
  contentA: string | null;
  contentB: string | null;
  inlineDiff: InlineDiffSegment[] | null;
  confidence: number;
  displayOrder: number;
  createdAt: string;
  /**
   * Computed field merged in by the route layer (Phase 3.2). Defaults to
   * 'pending' on the wire when no decision exists, but we still mark it
   * optional so older payloads / hand-crafted fixtures don't break.
   */
  currentStatus?: DecisionStatus;
  /**
   * P3-7: identity of the last writer on `vd_review_decision`. `null` when
   * `currentStatus === 'pending'`. Lets the reviewer verify the lawyer's
   * prior call without a second round-trip to GET /decisions.
   */
  decidedBy?: string | null;
  decidedByRole?: VeriDocRole | null;
  decidedAt?: string | null;
  /**
   * Computed field merged in by the route layer (Phase 4.3). Number of
   * annotations attached to this change. Optional so older payloads
   * (and fixtures) default to 0 without explicit handling.
   */
  annotationCount?: number;
  /**
   * P2-5: computed at read time by `detect_semantic_tags`. Subset of
   * `['amount', 'date', 'term']` — identifies whether this change touches
   * a key number (money / date / duration) so reviewers can filter on the
   * changes that matter most. Optional so older payloads default to no
   * tags.
   */
  semanticTags?: SemanticTag[];
}

/**
 * P2-5 — semantic tag identifying a "key number" dimension on a change.
 *
 * `amount`: money mention (元 / 万 / RMB / USD / EUR / € / ¥ etc.).
 * `date`: calendar date in ISO or Chinese long form.
 * `term`: duration with Chinese unit (天 / 个月 / 年 / 工作日 etc.).
 */
export type SemanticTag = 'amount' | 'date' | 'term';
export const SEMANTIC_TAGS: readonly SemanticTag[] = ['amount', 'date', 'term'] as const;

export interface AnnotationResponse {
  id: string;
  changeId: string;
  body: string;
  excerpt: string;
  authorId: string;
  authorRole: VeriDocRole;
  authorDisplayName: string;
  createdAt: string;
  updatedAt: string;
  /** ISO timestamp after which edit / delete is rejected by the server. */
  editWindowExpiresAt: string;
  /**
   * ADR-120 — author-declared read visibility. Always present on responses;
   * the fact that a reader received the row means it passed the server-side
   * ``filter_annotations_by_visibility`` check.
   */
  visibility: AnnotationVisibility;
}

/**
 * ADR-120 D1 — three explicit visibility values.
 *
 * - `private` (default): only the author can read.
 * - `team`: every authoring role (lawyer + reviewer) can read.
 * - `shared`: every role that can see the parent comparison can read.
 */
export type AnnotationVisibility = 'private' | 'team' | 'shared';

export interface AnnotationsListResponse {
  annotations: AnnotationResponse[];
}

export interface ReviewDecisionResponse {
  id: string;
  changeId: string;
  /** Persisted status — either 'accepted' or 'rejected' (never 'pending'). */
  status: 'accepted' | 'rejected';
  reviewerId: string;
  reviewerRole: VeriDocRole;
  reviewerDisplayName: string;
  /**
   * P2-6: optional reviewer rationale for the decision (e.g. "客户底线 90 天").
   * `null` when the reviewer did not write one. Cleared on reset-to-pending.
   * Frontend uses this to render an "add rationale" affordance on already-
   * decided changes without forcing a toggle cycle.
   */
  note: string | null;
  decidedAt: string;
}

/**
 * The PUT endpoint either creates / updates a decision (returning a full
 * `ReviewDecisionResponse`) **or** toggles the row off and returns the
 * pending envelope. Discriminate on `status === 'pending'` to branch.
 */
export type DecisionMutationResponse =
  | ReviewDecisionResponse
  | { changeId: string; status: 'pending' };

export interface DecisionsListResponse {
  decisions: ReviewDecisionResponse[];
}

/**
 * P3-2: one row from the append-only ``vd_review_decision_log`` audit trail.
 * ``action`` is the wire verb the caller sent; ``status`` is the persisted
 * noun after the op. ``prevStatus`` / ``prevReviewerId`` capture the
 * parent row's prior state for audit continuity (NULL on first decision or
 * when the previous op was a reset that deleted the row).
 */
export interface DecisionHistoryItem {
  id: string;
  changeId: string;
  action: 'accept' | 'reject' | 'pending';
  status: 'accepted' | 'rejected' | 'pending';
  reviewerId: string;
  reviewerRole: VeriDocRole;
  reviewerDisplayName: string;
  note: string | null;
  prevStatus: 'accepted' | 'rejected' | 'pending' | null;
  prevReviewerId: string | null;
  decidedAt: string;
}

export interface DecisionHistoryResponse {
  items: DecisionHistoryItem[];
}

export interface ComparisonResponse {
  id: string;
  versionAId: string;
  versionBId: string;
  name: string | null;
  status: 'processing' | 'ready' | 'error';
  createdBy: string;
  createdByRole: VeriDocRole;
  changeCount: number;
  createdAt: string;
  completedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  changes: ChangeResponse[];
}

/**
 * T16 split: ``GET /vd/comparisons/{id}`` now returns this metadata-only
 * shape. The full changes list moved to the paged ``/changes`` endpoint
 * (see :interface:`ChangeListResponse`). Aggregate counts (accepted /
 * rejected / annotation totals) let the workspace header render the
 * progress meter without an extra round-trip.
 */
export interface ComparisonMetaResponse {
  id: string;
  versionAId: string;
  versionBId: string;
  name: string | null;
  status: 'processing' | 'ready' | 'error';
  createdBy: string;
  createdByRole: VeriDocRole;
  changeCount: number;
  createdAt: string;
  completedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  acceptedCount: number;
  rejectedCount: number;
  annotationCount: number;
}

/**
 * T16 split: one row in the cursor-paginated change list. Identical shape
 * to :interface:`ChangeResponse` — the workspace search box + decision
 * init logic depend on contentA / contentB / currentStatus /
 * annotationCount being present on every item.
 */
export interface ChangeListItem extends ChangeResponse {}

/**
 * T16 split: envelope for ``GET /vd/comparisons/{id}/changes``.
 * ``nextCursor`` is opaque (server-encoded); pass it back as ``cursor``
 * to fetch the next page. ``null`` + ``hasMore: false`` marks the end.
 */
export interface ChangeListResponse {
  items: ChangeListItem[];
  nextCursor: string | null;
  hasMore: boolean;
  /**
   * P2-09: ``null`` unless the request opted into ``includeTotal=true``.
   * The workspace's "X of Y reviewed" meter should pass it on first
   * page load; cursor pagination itself does not need it.
   */
  totalCount: number | null;
}

/**
 * Query params for the paged change list (T16). ``cursor`` omitted on
 * the first page; ``limit`` defaults to 50 server-side, clamped at 200.
 * ``includeTotal`` (P2-09) opts into a full ``COUNT(*)`` — set to true
 * only when the caller needs the total row count (e.g. progress meter).
 */
export interface ChangeListQuery {
  cursor?: string | null;
  limit?: number;
  includeTotal?: boolean;
}

// ---------------------------------------------------------------------------
// Editing (S6.2 F1) — in-workspace node editing
// Kept in sync with server/veridoc_core/schemas/editing.py
// ---------------------------------------------------------------------------

/**
 * Redline op for one reconstructed segment:
 *   keep   — unchanged / decision-kept node (present in the clean document)
 *   insert — new node (drafter addition, or reviewer-inserted)
 *   delete — struck node (redline-only preview, not in the clean document)
 *   move   — relocated node
 */
export type RedlineOp = 'keep' | 'insert' | 'delete' | 'move';

/** drafter = produced by the v1→v2 diff/decision; reviewer = user edit op. */
export type RedlineAuthor = 'drafter' | 'reviewer';

export interface EditingRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface RedlineSegment {
  nodeId: string;
  op: RedlineOp;
  author: RedlineAuthor;
  nodeType: string;
  level: number | null;
  runs: EditingRun[];
  positionV1: number | null;
  positionV2: number | null;
  /** keep/insert/move are present in the clean tree and editable; delete is not. */
  editable: boolean;
}

export interface InapplicableOp {
  opId: string;
  nodeId: string;
  reason: string;
}

export interface EditingState {
  comparisonId: string;
  materializedAt: string;
  editOpCount: number;
  canEdit: boolean;
  segments: RedlineSegment[];
  inapplicableOps: InapplicableOp[];
}

export interface UndoEditResponse {
  undone: boolean;
  state: EditingState;
}

export type EditOpType = 'set_text' | 'insert_node' | 'delete_node';

export interface AppendEditPayload {
  runs?: EditingRun[];
  parentId?: string | null;
  index?: number;
  type?: string;
  level?: number | null;
}

export interface AppendEditRequest {
  opType: EditOpType;
  targetNodeId?: string | null;
  payload: AppendEditPayload;
}

// ---------------------------------------------------------------------------
// Source verification (F6 / T3) — kept in sync with
// server/veridoc_verify/schemas/verification.py and the verdict payload built
// by server/veridoc_verify/providers/legal/payload.py. Enum literals are the
// single source of truth in docs/data-dictionary.md (来源核对枚举).
// ---------------------------------------------------------------------------

/** verdict.existence (V1) — does the cited source exist. */
export type VerdictExistence = 'existing' | 'not-found' | 'unverifiable';

/** verdict.validity (V2) — current legal force of the matched statute. */
export type VerdictValidity =
  | 'in-force'
  | 'repealed'
  | 'amended'
  | 'unknown'
  | 'n/a';

/** verdict.consistency (V3) — paraphrase vs. statute meaning. */
export type VerdictConsistency =
  | 'consistent'
  | 'questionable'
  | 'inconsistent'
  | 'not-evaluated';

/** verdict.overall — composite status derived from the three dimensions. */
export type VerdictOverall = 'pass' | 'warning' | 'fail' | 'unverifiable';

/**
 * verdict.confidence — epistemic trustworthiness (F6 accuracy hardening).
 *
 * Lets the reviewer see which parts of the verdict come from hard data
 * (rule-based, V1/V2 source lookup) versus AI judgment (V3 LLM). Senior
 * lawyers can then weight reliance accordingly: a `rule-based` fail on a
 * repealed statute is a ground-truth fact; a `low` verdict means the AI
 * could not be reached and the lawyer must compare manually.
 */
export type VerdictConfidence = 'rule-based' | 'high' | 'medium' | 'low';

/**
 * One extracted citation (the claim being verified). `lawName` / `articleNo`
 * are null for vague references the extractor couldn't parse (marked
 * unverifiable) — only `rawText` and `nodeId` are guaranteed.
 */
export interface VerificationCitation {
  rawText: string;
  lawName: string | null;
  articleNo: string | null;
  nodeId: string;
}

/** The authoritative source record matched for this citation (null = no match). */
export interface MatchedStatute {
  lawName: string;
  articleNo: string;
  effectiveDate: string | null;
  version: string | null;
  fullText: string;
}

export interface Verdict {
  existence: VerdictExistence;
  validity: VerdictValidity;
  consistency: VerdictConsistency;
  overall: VerdictOverall;
  /**
   * F6 accuracy hardening — present on every verdict payload emitted by the
   * legal provider. Defensive code should still tolerate its absence on
   * payloads written before the field shipped.
   */
  confidence?: VerdictConfidence;
  rationale: string;
}

/**
 * The verdict payload persisted to `vd_node_annotation.payload`. The wire type
 * is loose (`dict[str, Any]`) server-side, but the legal provider always emits
 * this shape (payload.py). Render defensively all the same.
 */
export interface VerificationPayload {
  citation: VerificationCitation;
  matchedStatute: MatchedStatute | null;
  verdict: Verdict;
}

/** Drill-down context to locate the annotated node in the document (F6-6). */
export interface VerificationNodeRef {
  nodeType: string;
  level: number | null;
  positionV1: number | null;
  positionV2: number | null;
  excerptV1: string | null;
  excerptV2: string | null;
}

/** One attached verdict plus drill-down context (GET). */
export interface VerificationResult {
  id: string;
  nodeId: string;
  payload: VerificationPayload;
  /** True when the annotated node was struck in the materialized working tree. */
  nodeDeleted: boolean;
  nodeRef: VerificationNodeRef | null;
}

export interface VerificationResultsResponse {
  comparisonId: string;
  results: VerificationResult[];
}

/** One citation that degraded during a run (source-repo / LLM unreachable). */
export interface VerificationFailure {
  nodeId: string | null;
  reason: string | null;
}

/** Partial-success summary returned by a run (POST, ADR-114 D7).
 *
 * P3-6: `citationsExtracted` and `nodesExamined` let the UI distinguish
 * "no citations in the document" (business as usual) from "citations
 * found but every verification failed" (AI / source-repo outage):
 *
 * - `citationsExtracted === 0` → render "文档未识别到法律引用".
 * - `citationsExtracted > 0 && processedCount === 0` → render "AI 核对失败，请稍后重试".
 */
export interface VerificationSummary {
  comparisonId: string;
  processedCount: number;
  failedCount: number;
  failures: VerificationFailure[];
  citationsExtracted: number;
  nodesExamined: number;
}

// ---------------------------------------------------------------------------
// Change analysis (F7 / F8 / T4) — kept in sync with
// server/veridoc_changes/schemas/changes.py and the annotation payload built by
// server/veridoc_changes/analysis/{importance,summary}.py. Enum literals are the
// single source of truth in docs/data-dictionary.md (变更分析枚举, ADR-116).
// ---------------------------------------------------------------------------

/** F7 verdict — how much a single change matters (PRD G7). */
export type ImportanceLevel = 'substantive' | 'wording' | 'unclear';

/**
 * The importance payload persisted to `vd_node_annotation.payload`. The wire
 * type is loose (`dict[str, Any]`) server-side, but the analysis layer always
 * emits this shape. Render defensively all the same.
 */
export interface ImportancePayload {
  importance: ImportanceLevel;
  rationale: string;
  changeId: string;
  changeType: ChangeType;
}

/** Drill-down context to locate the classified node in the document (mirrors F6-6). */
export interface ImportanceNodeRef {
  nodeType: string;
  level: number | null;
  positionV1: number | null;
  positionV2: number | null;
  excerptV1: string | null;
  excerptV2: string | null;
}

/** One attached importance verdict plus drill-down context (GET). */
export interface ImportanceResult {
  id: string;
  nodeId: string;
  payload: ImportancePayload;
  /** True when the classified node was struck in the materialized working tree. */
  nodeDeleted: boolean;
  nodeRef: ImportanceNodeRef | null;
}

export interface ImportanceResultsResponse {
  comparisonId: string;
  results: ImportanceResult[];
}

/** One change that degraded during an F7 run (LLM unreachable / missing id). */
export interface ImportanceFailure {
  changeId: string | null;
  nodeId: string | null;
  reason: string | null;
}

/**
 * Partial-success summary returned by an F7 run (POST, ADR-114 D7).
 *
 * P3-1 — `degraded` is the unified retry flag shared with F8
 * (`ChangeSummary.degraded`). True only when the run made no forward
 * progress (`processedCount === 0 && failedCount > 0`); partial-success
 * runs stay `false` and list the per-change degradations in `failures`.
 */
export interface ImportanceSummary {
  comparisonId: string;
  processedCount: number;
  failedCount: number;
  failures: ImportanceFailure[];
  degraded: boolean;
}

/** One bullet point of the F8 revision summary. */
export interface SummaryPoint {
  text: string;
  /** Optional related `vd_change` ids (drill-down). */
  changeIds: string[];
}

/**
 * One failure recorded during an F8 run. Mirrors `ImportanceFailure` so the
 * frontend can reuse the same renderer; the comparison-level summary carries
 * no node anchor so `nodeId` / `changeId` stay null.
 */
export interface SummaryFailure {
  changeId: string | null;
  nodeId: string | null;
  reason: string | null;
}

/**
 * The F8 revision summary (POST returns the freshly generated one; GET the
 * persisted one). `generatedAt` is null when no summary exists yet (never run);
 * `degraded` is true when the last run's LLM call failed (partial-success).
 *
 * P3-1 — `failures` mirrors F7's failure list so the frontend can drive the
 * same retry hint from either endpoint (`degraded === true` ⟺
 * `failures.length > 0`).
 */
export interface ChangeSummary {
  comparisonId: string;
  points: SummaryPoint[];
  degraded: boolean;
  failures: SummaryFailure[];
  generatedAt: string | null;
}

export const veridocApi = {
  // ---- Document version detail (parse tree) ----------------------------
  getVersion: (versionId: string) =>
    api.get<DocumentVersionDetail>(`/versions/${versionId}`),

  // ---- Comparisons ------------------------------------------------------
  createComparison: (
    payload: { versionAId: string; versionBId: string; name?: string },
  ) => api.post<ComparisonResponse>('/comparisons', payload),

  /**
   * @deprecated T16: returns metadata + aggregate counts only. Use
   * :func:`getComparisonMeta` + :func:`getComparisonChanges` for the
   * split-payload flow. This method is retained for the 2-week sunset
   * window — internally it now hits the same metadata-only endpoint
   * (``GET /comparisons/{id}`` without ``?include=changes``), so the
   * returned ``changes`` array is empty. Callers that need the legacy
   * full payload should switch to the new endpoints rather than pass
   * ``?include=changes`` manually.
   */
  getComparison: (comparisonId: string) =>
    api.get<ComparisonResponse>(`/comparisons/${comparisonId}`),

  /**
   * T16: fetch comparison metadata + aggregate counts (acceptedCount /
   * rejectedCount / annotationCount) without the changes list. Pair with
   * :func:`getComparisonChanges` for the full workspace payload.
   */
  getComparisonMeta: (comparisonId: string) =>
    api.get<ComparisonMetaResponse>(`/comparisons/${comparisonId}`),

  /**
   * T16: fetch one page of the comparison's change list. Cursor is
   * opaque — pass ``nextCursor`` back as ``cursor`` for the next page.
   * Pass ``limit`` to override the default page size (50); the server
   * clamps at 200. Pass ``includeTotal: true`` (P2-09) to get a full
   * ``totalCount`` — otherwise ``totalCount`` is ``null`` (cursor
   * pagination does not need it; the workspace should pull the total
   * from ``getComparisonMeta`` when the header is already being fetched).
   */
  getComparisonChanges: (comparisonId: string, query: ChangeListQuery = {}) => {
    const params = new URLSearchParams();
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.includeTotal) params.set('include_total', 'true');
    const qs = params.toString();
    const path = `/comparisons/${comparisonId}/changes`;
    return api.get<ChangeListResponse>(qs ? `${path}?${qs}` : path);
  },

  // ---- Editing (S6.2 F1) ------------------------------------------------
  /**
   * Enter editing: materialize (freeze, one-shot) the working tree and
   * return the reconstructed state. lawyer/reviewer only (viewer → 403).
   */
  enterEditing: (comparisonId: string) =>
    api.post<EditingState>(`/comparisons/${comparisonId}/working-tree`),

  /**
   * Read the current reconstructed editing state. 404 (code
   * `VD_COMPARISON_NOT_MATERIALIZED`) when nobody has entered editing yet.
   */
  getEditingState: (comparisonId: string) =>
    api.get<EditingState>(`/comparisons/${comparisonId}/working-tree`),

  /** Append one edit op; returns the recomputed editing state. */
  appendEdit: (comparisonId: string, req: AppendEditRequest) =>
    api.post<EditingState>(`/comparisons/${comparisonId}/edits`, req),

  /** Undo the most recent edit (append-only compensating op). */
  undoEdit: (comparisonId: string) =>
    api.post<UndoEditResponse>(`/comparisons/${comparisonId}/edits/undo`),

  // ---- Review decisions (M3 Phase 3.2 / 3.6) ---------------------------
  /**
   * Accept or reject a change. Re-asserting the current decision toggles
   * back to `pending` (response shape becomes `{changeId, status:'pending'}`).
   * Use `DecisionMutationResponse` discriminator on the caller side.
   *
   * P2-6: passing a non-empty `note` skips the toggle-off check on the
   * server so the caller can attach or update a rationale on an existing
   * decision (the same-status PUT updates `note` in place rather than
   * flipping to pending). Empty / whitespace-only notes are omitted from
   * the body to preserve the default toggle semantics.
   */
  putDecision: (changeId: string, action: DecisionAction, note?: string) => {
    const trimmed = note?.trim();
    return api.put<DecisionMutationResponse>(`/changes/${changeId}/decision`, {
      status: action,
      ...(trimmed ? { note: trimmed } : {}),
    });
  },

  /** List every active decision for a comparison (no pending rows). */
  getDecisions: (comparisonId: string) =>
    api.get<DecisionsListResponse>(`/comparisons/${comparisonId}/decisions`),

  /**
   * P3-2: append-only flip history for one change. Readable by every role
   * (incl. viewer) that can see the parent change; writes go through
   * `putDecision`. Ordered oldest-first so the UI can render top-to-bottom.
   */
  getDecisionHistory: (changeId: string) =>
    api.get<DecisionHistoryResponse>(`/changes/${changeId}/decision-history`),

  // ---- Export (M4 Phase 4.5) -------------------------------------------
  /**
   * Build the full export URL for one comparison + format. The browser
   * navigates to it directly so the response stream can be saved as a
   * file without buffering the bytes in JavaScript memory. Resolves against
   * the configured base (transport seam), not a hardcoded `/vd`.
   */
  exportUrl: (
    comparisonId: string,
    format: 'md' | 'pdf' | 'docx' | 'redline' | 'package' | 'client-summary',
  ) =>
    `${veridocBaseUrl()}/comparisons/${comparisonId}/export?format=${format}`,

  // ---- Annotations (M4 Phase 4.3 / 4.4) --------------------------------
  /** List every annotation attached to a single change. */
  listChangeAnnotations: (changeId: string) =>
    api.get<AnnotationsListResponse>(`/changes/${changeId}/annotations`),

  /** List every annotation across a comparison (history page / export). */
  listComparisonAnnotations: (comparisonId: string) =>
    api.get<AnnotationsListResponse>(
      `/comparisons/${comparisonId}/annotations`,
    ),

  /**
   * Create a new annotation.
   *
   * ADR-120 — ``visibility`` defaults to ``private`` (least-exposure
   * baseline). The frontend renders an explicit selector so the author
   * opts into ``team`` / ``shared``.
   */
  createAnnotation: (
    changeId: string,
    body: string,
    visibility: AnnotationVisibility = 'private',
  ) =>
    api.post<AnnotationResponse>(
      `/changes/${changeId}/annotations`,
      { body, visibility },
    ),

  /** Edit an existing annotation (author + within edit window). */
  updateAnnotation: (annotationId: string, body: string) =>
    api.patch<AnnotationResponse>(`/annotations/${annotationId}`, { body }),

  /** Delete an annotation (author + within edit window). */
  deleteAnnotation: (annotationId: string) =>
    api.delete<void>(`/annotations/${annotationId}`),

  // ---- Source verification (F6 / T3) -----------------------------------
  /**
   * Run legal source verification over a comparison. lawyer/reviewer only
   * (viewer → 403). Returns a partial-success summary — individual
   * source-repo / LLM failures degrade that citation without aborting.
   */
  runSourceVerification: (comparisonId: string) =>
    api.post<VerificationSummary>(
      `/comparisons/${comparisonId}/source-verification`,
    ),

  /**
   * List attached verdicts + drill-down context for a comparison. All roles
   * (viewer included). Empty `results` means "verified, nothing found" or
   * "not yet run".
   */
  getSourceVerification: (comparisonId: string) =>
    api.get<VerificationResultsResponse>(
      `/comparisons/${comparisonId}/source-verification`,
    ),

  // ---- Change analysis (F7 / F8 / T4) ----------------------------------
  /**
   * Run F7 change-importance classification over a comparison. lawyer/reviewer
   * only (viewer → 403). Returns a partial-success summary — individual changes
   * the LLM dropped or that degraded count toward `failedCount` without
   * aborting the run.
   */
  runChangeImportance: (comparisonId: string) =>
    api.post<ImportanceSummary>(
      `/comparisons/${comparisonId}/change-importance`,
    ),

  /**
   * List attached importance verdicts + drill-down context. All roles. Empty
   * `results` means "analyzed, nothing classified" or "not yet run".
   */
  getChangeImportance: (comparisonId: string) =>
    api.get<ImportanceResultsResponse>(
      `/comparisons/${comparisonId}/change-importance`,
    ),

  /**
   * Generate the F8 revision summary. lawyer/reviewer only (viewer → 403). An
   * LLM failure degrades to an empty `degraded` summary rather than erroring.
   */
  runChangeSummary: (comparisonId: string) =>
    api.post<ChangeSummary>(`/comparisons/${comparisonId}/change-summary`),

  /**
   * Read the persisted revision summary. All roles. `generatedAt === null`
   * means the comparison was never analyzed.
   */
  getChangeSummary: (comparisonId: string) =>
    api.get<ChangeSummary>(`/comparisons/${comparisonId}/change-summary`),
};
